import { faGift, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Icons } from '~/features/stake/assets/icons'
import { useBalance } from '../api'
import { useCashier } from '../hooks/useCashier'
import { formatAmount, formatAmountExact } from '../utils'
import { FundsBreakdown } from './FundsBreakdown'
import { PointsBreakdown } from './PointsBreakdown'

const coinIcon = new URL('../assets/coin.png', import.meta.url).toString()

/**
 * One labelled cell of the drawer panel.
 *
 * The three figures used to be three floating black pills carrying a mark and a number each, and
 * nothing else — so the only thing telling you which was your spendable balance, which was bonus
 * and which was points was recognising three small icons. Two of them are money and look alike at
 * 20px. The label is the fix; the mark stays because the colour vocabulary in `Funds.tsx` (white on
 * the coin for spendable, amber on a gift for bonus) is load-bearing — bonus money cannot be spent
 * in the store, and that has to be legible before anyone reads a word.
 *
 * Renders as a link, a button or an inert div depending on what it does, rather than making
 * everything a button and wiring navigation through a click handler.
 */
const Cell = ({
    label,
    mark,
    value,
    tone = '',
    to,
    onClick,
    onNavigate,
    ariaLabel,
    title,
}: {
    label: string
    mark: ReactNode
    value: ReactNode
    tone?: string
    to?: string
    onClick?: () => void
    onNavigate?: () => void
    ariaLabel?: string
    title?: string
}) => {
    const body = (
        <>
            <span className='text-[0.5625rem] uppercase tracking-[0.18em] text-white/50'>{label}</span>
            <span className={`flex flex-row items-center gap-1.5 text-sm font-bold tabular-nums ${tone}`}>
                {mark}
                {value}
            </span>
        </>
    )
    const shell = 'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-2 py-2.5 min-h-[3.25rem]'

    if (to) {
        return (
            <Link to={to} onClick={onNavigate} aria-label={ariaLabel} title={title} className={shell}>
                {body}
            </Link>
        )
    }
    if (onClick) {
        return (
            <button type='button' onClick={onClick} aria-label={ariaLabel} title={title} className={shell}>
                {body}
            </button>
        )
    }
    return (
        <div className={shell} title={title}>
            {body}
        </div>
    )
}

/**
 * `onNavigate` is the drawer's hook, and carries the same contract it does on `CashierButton`: the
 * mobile menu is a full-height fixed panel, so anything in here that changes the route or opens a
 * modal has to close it first. Without it the route changed *behind* the open drawer and the tap
 * read as doing nothing at all — which is exactly how the points figure was reported.
 */
export const BalanceMenuWidget = ({ onNavigate }: { onNavigate?: () => void }) => {
    const { data: balance, isLoading, isFetched } = useBalance()
    // The gates used to be written out here. They now live in `useCashier` with every other entry
    // point, which is what stopped this one from blocking deposits whenever withdrawals were off.
    const { open } = useCashier()
    const modalId = useMemo(() => Math.random().toString(36), [])

    const showModal = () => {
        onNavigate?.()
        open('deposit')
    }

    /*
      Where the points figure sends you depends on whether you have any: none means the useful next
      step is earning them, so Inventory (`/renting` — the route kept its old name when the page was
      renamed); any at all means the useful step is spending them, so the Store. `INVENTORY_PAGE.md`
      records "Points → Store" as the intended routing and frames the loop the same way.
    */
    const points = balance?.point ?? 0
    const hasPoints = points > 0
    const bonus = balance?.airdrop ?? 0
    const spinner = <FontAwesomeIcon icon={faSpinner} spin />

    /*
      `formatAmount` keeps the grouped 2dp form until 10,000 and switches to a compact unit above
      it. Points is the one figure here with no ceiling: 454,052.25 rendered into a third of a
      phone drawer is where the truncated `454,0...` came from, and it now reads `454K`.

      The exact figure still goes on `title` and `aria-label`, because `PointsBreakdown` is
      desktop-only -- on a phone this cell is the only place the number appears at all.
    */

    const pointsLabel = `BR1 Points: ${formatAmountExact(points)}. ${
        hasPoints ? 'Spend them in the Store' : 'Start earning them in Inventory'
    }`

    return (
        <>
            {/*
              Phone: one panel, three labelled columns, hairline dividers.

              Three separate pills read as three unrelated controls floating on the gradient, and
              two of them were the same colour and shape. One bordered object says these are three
              readings of the same thing — your account — which is what they are.
            */}
            <div className='md:hidden flex flex-row items-stretch rounded-2xl border border-white/15 bg-black/80 divide-x divide-white/10 overflow-hidden'>
                <Cell
                    label='Funds'
                    mark={<img src={coinIcon} alt='' className='h-4 w-auto' />}
                    value={isLoading ? spinner : formatAmount(balance?.usdc ?? 0)}
                    onClick={showModal}
                    ariaLabel={`Spendable funds: ${formatAmountExact(balance?.usdc ?? 0)}. Deposit or withdraw`}
                    title={formatAmountExact(balance?.usdc ?? 0)}
                />
                {!isLoading && bonus > 0 && (
                    <Cell
                        label='Bonus'
                        mark={<FontAwesomeIcon icon={faGift} className='text-[0.9em]' />}
                        value={formatAmount(bonus)}
                        tone='text-amber-400'
                        title={`${formatAmountExact(bonus)} - bonus funds: spawn fees only, not valid for store purchases`}
                    />
                )}
                {/*
                  Inert until the balance lands. `points` reads 0 while the query is in flight, so a
                  tap in that window evaluated `hasPoints` as false and sent a player holding 30,000
                  points to Inventory to "start earning".
                */}
                <Cell
                    label='Points'
                    mark={<Icons.SkullPoint className='h-4 w-4' />}
                    value={isLoading ? spinner : formatAmount(points)}
                    to={isFetched ? (hasPoints ? '/store' : '/renting') : undefined}
                    onNavigate={onNavigate}
                    ariaLabel={isFetched ? pointsLabel : 'BR1 Points: loading'}
                    title={formatAmountExact(points)}
                />
            </div>

            {/* ---------- desktop bar ---------- */}
            <div className='hidden md:flex flex-row-reverse items-center gap-x-4 gap-y-1 font-bold'>
                <div className='relative group/funds flex flex-row-reverse gap-x-4 gap-y-1'>
                    {/*
                      Desktop shows the total; the hover panel underneath breaks it into spendable
                      and bonus. Splitting it in the bar as well left the panel with nothing to add.

                      Worth knowing why it was split in the first place: bonus funds cannot be spent
                      in the store, so a combined figure quotes money you cannot fully use. That risk
                      is now carried by `FundsBreakdown`, which names both balances and says where
                      each one works — so the total can lead, with the detail one hover away.
                    */}
                    <button
                        id={modalId}
                        aria-label={`Funds: ${formatAmountExact(balance?.totalInGame ?? 0)} total. Deposit or withdraw`}
                        title={formatAmountExact(balance?.totalInGame ?? 0)}
                        onClick={showModal}
                        className='group flex flex-row-reverse items-center gap-2 p-0'
                    >
                        {/* Compacted like the phone panel. The bar is glanceable chrome, and the
                            hover breakdown below already carries the exact figure. */}
                        <div className='text-right group-hover:underline underline-offset-2'>
                            {isLoading ? spinner : formatAmount(balance?.totalInGame ?? 0)}
                        </div>
                        <div className='h-8 w-10 shrink-0'>
                            <img src={coinIcon} alt='' className='mx-auto h-full' />
                        </div>
                    </button>

                    <FundsBreakdown />
                </div>

                <div className='relative group/points'>
                    {/*
                      A link, not a button with `navigate()` in a click handler. It performs a
                      navigation, so it should behave like every other one on the site: middle-click
                      and ctrl-click open a tab, and the browser previews the destination in the
                      status bar.
                    */}
                    {isFetched ? (
                        <Link
                            to={hasPoints ? '/store' : '/renting'}
                            onClick={onNavigate}
                            aria-label={pointsLabel}
                            title={formatAmountExact(points)}
                            className='group flex flex-row-reverse items-center gap-2'
                        >
                            <div className='flex-1 text-right group-hover:underline underline-offset-2'>
                                {isLoading ? spinner : formatAmount(points)}
                            </div>
                            <div className='flex h-10 w-10 shrink-0 flex-row items-center'>
                                <Icons.SkullPoint className='mx-auto h-8 w-8' />
                            </div>
                        </Link>
                    ) : (
                        <span className='group flex flex-row-reverse items-center gap-2' aria-label='BR1 Points: loading'>
                            <div className='flex-1 text-right'>{spinner}</div>
                            <div className='flex h-10 w-10 shrink-0 flex-row items-center'>
                                <Icons.SkullPoint className='mx-auto h-8 w-8' />
                            </div>
                        </span>
                    )}

                    <PointsBreakdown />
                </div>
            </div>
        </>
    )
}
